import { css, styled } from "styled-components";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { cssFontSize, cssSpacing, surfaceColorMap } from "../../util/styles.js";
import type { BackgroundColor } from "../../types/theme.js";

const ease = "cubic-bezier(.32, .72, .26, 1)";

export const Title = styled.h2`
  margin: 0 0 ${cssSpacing("s")};
  color: var(--servicegrid-v2-title-color, var(--swatch2));
  font-size: ${cssFontSize("large")};
  font-weight: 600;
  line-height: 1.3333;

  @media (min-width: ${mobileBreakPointCSS}) {
    margin-bottom: ${cssSpacing("m")};
    font-size: ${cssFontSize("larger")};
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${cssSpacing("s")};

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const surfaceCss = (bg: BackgroundColor) => css`
  --knockout: ${surfaceColorMap[bg]};
  background-color: ${surfaceColorMap[bg]};
  color: ${bg === "surface3" ? "#fff" : "var(--swatch2)"};
`;

const defaultCardCss = css`
  --knockout: var(--servicegrid-v2-card-background, var(--swatch3));
  background-color: var(--servicegrid-v2-card-background, var(--swatch3));
  color: var(--servicegrid-v2-card-text-color, var(--swatch2));
`;

export const Card = styled.a<{ $bg?: BackgroundColor }>`
  ${(p) => (p.$bg ? surfaceCss(p.$bg) : defaultCardCss)}
  --icon: var(--servicegrid-v2-icon-color, var(--swatch1));
  --anim-state: paused;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${cssSpacing("xs")};
  min-width: 0;
  height: 120px;
  padding: 0 ${cssSpacing("s")};
  border-radius: ${(p) => p.theme.borderRadius};
  text-decoration: none;
  transition: background-color 0.28s ${ease}, color 0.28s ${ease};

  &:hover,
  &:focus-visible {
    --icon: var(--servicegrid-v2-card-hover-text-color, #fff);
    --knockout: var(--servicegrid-v2-card-hover-background, var(--swatch1));
    --anim-state: running;
    background-color: var(
      --servicegrid-v2-card-hover-background,
      var(--swatch1)
    );
    color: var(--servicegrid-v2-card-hover-text-color, #fff);
  }
`;

export const CardTitle = styled.span`
  font-size: ${cssFontSize("large")};
  font-weight: 600;
  line-height: 1.3333;
  text-decoration-line: underline;
  text-underline-position: from-font;
  transition: text-decoration-color 0.28s ${ease};

  ${Card}:hover &,
  ${Card}:focus-visible & {
    text-decoration-color: transparent;
  }
`;

export const CardIcon = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: 70px;
    height: 70px;
    overflow: visible;
    color: var(--icon);
    transition: color 0.28s ${ease}, transform 0.35s ${ease};
  }

  svg [id*="fill-primary" i],
  svg [id*="fill-primary" i] * {
    fill: currentColor;
  }

  svg [id*="stroke-primary" i],
  svg [id*="stroke-primary" i] * {
    fill: none;
    stroke: currentColor;
  }

  svg [id*="fill-white" i],
  svg [id*="fill-white" i] * {
    fill: #fff;
    transition: fill 0.28s ${ease};
  }

  svg * {
    animation-play-state: var(--anim-state, paused) !important;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    z-index: -1;
    aspect-ratio: 1;
    border-radius: 50%;
    opacity: 0;
    transition: transform 0.5s ${ease}, opacity 0.4s ${ease};
  }

  &::before {
    top: 50%;
    left: 50%;
    width: 235%;
    background: rgba(255, 255, 255, 0.13);
    transform: translate(-50%, -50%) scale(0.35);
  }

  &::after {
    bottom: -52%;
    left: -46%;
    width: 118%;
    background: color-mix(
      in srgb,
      var(--servicegrid-v2-icon-halo-color, var(--secondary-icon-color)) 42%,
      transparent
    );
    transform: scale(0.35);
    transition-delay: 70ms;
  }

  ${Card}:hover &::before,
  ${Card}:focus-visible &::before {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }

  ${Card}:hover &::after,
  ${Card}:focus-visible &::after {
    opacity: 1;
    transform: scale(1);
  }

  ${Card}:hover & svg,
  ${Card}:focus-visible & svg {
    transform: scale(1.1);
  }

  ${Card}:hover & svg [id*="fill-white" i],
  ${Card}:hover & svg [id*="fill-white" i] *,
  ${Card}:focus-visible & svg [id*="fill-white" i],
  ${Card}:focus-visible & svg [id*="fill-white" i] * {
    fill: var(--servicegrid-v2-card-hover-background, var(--swatch1));
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after,
    svg {
      transition-duration: 0.01ms;
    }

    svg * {
      animation: none !important;
    }
  }
`;
